import React from 'react';
import { User } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import type { Process } from '../type/Process';
import { CollapsibleFormSection } from './CollapsibleFormSection';
import { ComboboxInput } from '@/components/form/inputs/ComboboxInput';
import { OPERATION_OPTIONS } from '../data/operationOptions';

type Props = {
  formProcess: Process;
  setFormProcess: React.Dispatch<React.SetStateAction<Process>>;
  mandatoryFields: string[];
  onToggleMandatory: (key: string) => void;
};

export const ProcessFormOperatorSection: React.FC<Props> = ({
  formProcess,
  setFormProcess,
  mandatoryFields,
  onToggleMandatory,
}) => {
  const { t } = useTranslation();

  const fields = ['operation'] as const;
  const setCount = fields.filter((f) => formProcess[f] !== null).length;
  const defaultOpen = setCount > 0;

  const countLabel = t('traceability.processManager.form.sections.countLabel', {
    set: setCount,
    total: fields.length,
  });

  return (
    <CollapsibleFormSection
      icon={<User className="h-3 w-3" aria-hidden />}
      title={t('traceability.processManager.form.sections.operatorInfo.title')}
      toggleLabel={t('traceability.processManager.form.sections.operatorInfo.toggleLabel')}
      countLabel={countLabel}
      defaultOpen={defaultOpen}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <div className="flex items-center justify-between gap-2 mb-1">
            <label htmlFor="process-operation" className="text-sm font-medium text-foreground">
              {t('traceability.processManager.form.prefillFields.operation.label')}
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={mandatoryFields.includes('operation')}
                onChange={() => onToggleMandatory('operation')}
                className="h-3.5 w-3.5 rounded border-border accent-primary"
              />
              {t('traceability.processManager.form.showToOperator')}
            </label>
          </div>
          <ComboboxInput
            name="process-operation"
            label=""
            value={formProcess.operation}
            onChange={(val) => setFormProcess((p) => ({ ...p, operation: val }))}
            options={OPERATION_OPTIONS}
            placeholder={t(
              'traceability.processManager.form.prefillFields.operation.placeholder'
            )}
          />
        </div>
      </div>
    </CollapsibleFormSection>
  );
};
